import { createSimpleContext } from "@mux/ui/context";
import { createEffect, createMemo, createSignal, onCleanup, untrack } from "solid-js";
import { createStore, reconcile } from "solid-js/store";
import { getFleetBootstrap, getMachineMetrics, issueMachineAccessBatch } from "@/transport/fleet";
import { setAccountMachineResolver } from "@/transport/account-machine";
import { errorMessage } from "@/transport/http";
import { eventCursorReached } from "@/utils/event-cursor";
import { embeddedAppInfo } from "@/transport/app-info";
import { ServerConnection, useServer } from "@/context/server";
import { invalidateProviderUsage } from "@/hooks/use-provider-usage";
import { invalidateProviders } from "@/hooks/use-providers";
import { invalidateAccountPreferences } from "@/context/account-preference";
import { invalidateAutomations } from "@/context/automations-revision";
import { invalidateWorkspaceInventory } from "@/context/workspace-inventory";
import { machineRouteManager, preferredRouteCheckMs } from "@/fleet/machine-route-manager";
import { createRouteComparisonController } from "@/fleet/route-comparison-controller";
import { fastestProvider, preferredPublicRoute } from "@/fleet/route-latency";
import { createRoutePinPreference } from "@/storage/route-pin-preference";
import { createRouteMemory } from "@/storage/route-memory";
import { createMachineAccessController } from "@/fleet/machine-access-controller";
import { createMachineAccessBatcher } from "@/fleet/machine-access-batch";
import { createFleetEventStreamController } from "@/fleet/event-stream-controller";
import { pageIsVisible } from "@/utils/network-schedule";
import { watchSystemWake } from "@/utils/system-wake";
import { parseMachineFromPath } from "@/utils/machine-route";
import { readFleetSnapshot, seedFleetSnapshot } from "@/utils/fleet-cache";
import { createQuery } from "@/utils/query";
import { reuseEqual } from "@/utils/reuse-equal";
import { formatMachineDisplayName } from "@/utils/machine-format";
import { applyCapabilityLibraryChange } from "@/context/capability-library";
import { applyAccountMuxOracleChange } from "@/context/account-mux-oracle";
import { localMode } from "@/transport/local-mode";
import {
  completeMachineHealth,
  effectiveMachineHealth,
  enrollmentProgressFromFleetEvent,
  MACHINE_HEARTBEAT_STALE_SECONDS,
  machineKey,
  machineReady,
  mergeFleetSnapshots,
  retainFleetSnapshotOnFailure,
  withAccessFailure,
  withAccessGrants,
  withHeartbeat,
  withMetrics,
  withMetricsSnapshot,
  withUpdate,
  machineBaseUrl,
  type FleetEnrollmentProgress,
  type FleetHeartbeatUpdate,
  type FleetMachine,
  type FleetMachineHealth,
  type FleetMachineMetrics,
  type FleetMachineView,
  type FleetUpdate,
} from "./fleet-machine";
import { createFleetRouteController } from "./fleet-routes";

export {
  completeMachineHealth,
  effectiveMachineHealth,
  enrollmentProgressFromFleetEvent,
  machineFunky,
  machineKey,
  machineLastSeenAt,
  machineReady,
  machineRouteLabel,
  machineUrl,
  mergeFleetSnapshots,
  retainFleetSnapshotOnFailure,
} from "./fleet-machine";
export type {
  FleetEnrollmentProgress,
  FleetMachine,
  FleetMachineHealth,
  FleetMachineMetrics,
  FleetMachineView,
  MachineRouteKind,
} from "./fleet-machine";

/** Grants missing or ending within this window renew alongside any other machine's. */
const COMPANION_RENEWAL_WINDOW_MS = 5 * 60_000;

async function fetchMachineSnapshots(access: boolean) {
  const snapshot = await getFleetBootstrap({ access });
  const metrics = new Map(snapshot.metrics.map((item) => [item.name, item]));
  return {
    machines: snapshot.machines.map((machine): FleetMachine => ({
      ...machine,
      metrics: metrics.get(machine.name),
      health: completeMachineHealth(machine.health),
    })),
    events: snapshot.events,
    access: snapshot.access,
  };
}

/** The fleet event mark a `server.connected` event carries. */
const subscribedEvents = (properties: unknown) => {
  const events = (properties as { events?: unknown } | undefined)?.events;
  return typeof events === "string" ? events : undefined;
};

export const { use: useFleet, provider: FleetProvider } = createSimpleContext({
  name: "Fleet",
  init: () => {
    const server = useServer();
    const standalone = localMode();
    const accountFleet = !standalone;
    const localDevelopment = import.meta.env.VITE_MUX_LOCAL_DEV === "1";
    const startedAt = Math.floor(Date.now() / 1000);
    // A test deployment shortens the heartbeat window, and checks it as often.
    const offlineAfter = (standalone ? undefined : embeddedAppInfo()?.machineOfflineAfterSeconds) ?? MACHINE_HEARTBEAT_STALE_SECONDS;
    const [healthClock, setHealthClock] = createSignal(startedAt);
    const healthClockTimer = window.setInterval(
      () => setHealthClock(Math.floor(Date.now() / 1000)),
      Math.min(10_000, offlineAfter * 500),
    );
    const cachedMachines = accountFleet ? readFleetSnapshot() ?? [] : [];
    const routeMachine = typeof location === "undefined" ? undefined : parseMachineFromPath(location.pathname);
    const cachedRouteMachine = routeMachine
      ? cachedMachines.find((machine) => machine.name === routeMachine)
      : undefined;
    const initialHealth = Object.fromEntries(
      cachedMachines.flatMap((machine) =>
        machine.health ? [[machineKey(machine.public_url), machine.health] as const] : []),
    );
    const [health, setHealth] = createStore<
      Record<string, FleetMachineHealth | undefined>
    >(initialHealth);
    // Machines whose own event stream is live, counted per report.
    const [liveStreams, setLiveStreams] = createStore<Record<string, number>>({});
    /**
     * Machines on screen: the route's (or, on a page without one, the selected
     * machine) and every Grid window's. Each keeps its access renewed and its
     * routes measured, and moves to a better route when one answers; moving
     * between them restarts none of it.
     */
    const [shown, setShown] = createSignal<readonly string[]>([], {
      equals: (a, b) => a.length === b.length && a.every((name, index) => name === b[index]),
    });
    // The fleet event mark the last loaded snapshot was read at: the control
    // plane takes it before reading, so the snapshot holds every earlier event.
    let snapshotEvents: string | undefined;
    // When no cached grant is usable without renewal, the first snapshot brings
    // grants, so opening a machine does not wait for a separate access request.
    let snapshotAccess = accountFleet && !cachedMachines.some((machine) =>
      !!machine.accessToken && (machine.accessTokenExpiresAt ?? 0) * 1000 - Date.now() > 120_000);
    async function loadMachines(): Promise<FleetMachine[]> {
      const access = snapshotAccess;
      snapshotAccess = false;
      const next = await fetchMachineSnapshots(access);
      snapshotEvents = next.events;
      const names = next.machines.map(machine => machine.name);
      routePins.retain(names);
      routeMemory.retain(names);
      // Read after the request: access may have been renewed or cleared while it was pending.
      const merged = mergeFleetSnapshots(untrack(machines) ?? [], next.machines);
      const granted = next.access ? withAccessGrants(merged, next.access) : merged;
      seedFleetSnapshot(granted);
      return granted;
    }
    let refreshingFleet: Promise<FleetMachine[]> | undefined;
    const machines = createQuery(
      () => true,
      (): Promise<FleetMachine[]> => {
        refreshingFleet = (accountFleet ? retainFleetSnapshotOnFailure(loadMachines(), () => untrack(machines) ?? cachedMachines) : Promise.resolve([]))
          .finally(() => { refreshingFleet = undefined; });
        return refreshingFleet;
      },
      { initialValue: cachedMachines },
    );
    /** Applies a change to the loaded machine list; a no-op before it loads. */
    const mutateMachines = (change: (current: FleetMachine[]) => FleetMachine[], persist = false) => {
      const current = machines();
      if (!current) return;
      machines.mutate(change(current));
      if (persist) seedFleetSnapshot(machines() ?? []);
    };
    const [enrollmentProgress, setEnrollmentProgress] =
      createSignal<FleetEnrollmentProgress>();
    const [startedEmpty, setStartedEmpty] = createSignal(false);
    let observedInitialFleet = false;
    createEffect(() => {
      if (machines.loading || observedInitialFleet) return;
      observedInitialFleet = true;
      setStartedEmpty((machines() ?? []).length === 0);
    });
    // One request renews every machine that needs a grant, not one per machine.
    const accessBatch = createMachineAccessBatcher({
      issueMany: issueMachineAccessBatch,
      companions: () => (untrack(() => machines()) ?? [])
        .filter((machine) => (machine.accessTokenExpiresAt ?? 0) * 1000 - Date.now() < COMPANION_RENEWAL_WINDOW_MS)
        .map((machine) => machine.name),
      adopt: (name, grant) => accessController.adopt(name, { token: grant.token, expiresAt: grant.expires_at }),
    });
    const accessController = createMachineAccessController({
      read: (name) => untrack(() => machines())?.find((machine) => machine.name === name),
      refresh: async (machine, signal) => {
        const access = await accessBatch.request(machine.name, signal);
        return { ...machine, auth: undefined, accessToken: access.token, accessTokenExpiresAt: access.expires_at, accessError: undefined };
      },
      update: (machine) => {
        machines.mutate((machines() ?? []).map((item) => item.name === machine.name ? machine : item));
        seedFleetSnapshot(machines() ?? []);
      },
      failed: (name, error, usable) => {
        machines.mutate((machines() ?? []).map((machine) =>
          machine.name !== name ? machine : withAccessFailure(machine, errorMessage(error, "Could not issue machine access credentials"), usable)));
        seedFleetSnapshot(machines() ?? []);
      },
    });
    onCleanup(accessController.dispose);

    const ensureMachineAccess = accessController.ensure;
    const routes = createFleetRouteController({ server, machines, shown, ensureMachineAccess, enabled: !standalone });
    const machineConnection = routes.machineConnection;

    const ensureMachineConnection = async (name: string, callerSignal?: AbortSignal) => {
      const signal = routes.routeSignal(callerSignal);
      if (signal.aborted) return undefined;
      if (standalone) return name === "local" ? server.current : undefined;
      return routes.connect(name, signal);
    };

    /**
     * Installs a registered machine's transport, which needs no grant or
     * verified route: its requests renew access through `prepare` and recover
     * onto a working route. A machine that cannot be reached yet still has its
     * connection, so the connection gate retries it instead of nothing doing so.
     */
    const installTransport = (machine: FleetMachine) => {
      if (!machineBaseUrl(machine)) return undefined;
      return server.add(
        { type: "http", displayName: machine.name, http: machineConnection(machine) },
        { activate: false, persist: false },
      );
    };

    const routePins = createRoutePinPreference();
    const routeMemory = createRouteMemory();
    if (cachedRouteMachine && machineBaseUrl(cachedRouteMachine)) {
      // Start on the route this browser preferred last time; selection still verifies it.
      // A cached copy without an access grant renews one on its first request.
      machineRouteManager.prefer(cachedRouteMachine.name, preferredPublicRoute(cachedRouteMachine, {
        pinned: routePins.pinned(cachedRouteMachine.name),
        fastest: routeMemory.fastest(cachedRouteMachine.name),
      })?.url);
      server.add(
        { type: "http", displayName: cachedRouteMachine.name, http: machineConnection(cachedRouteMachine) },
        { activate: true, persist: false },
      );
    }

    let refreshingMetrics: Promise<void> | undefined;
    const refreshMetrics = (forceAfterPending = false): Promise<void> => {
      if (!accountFleet) return Promise.resolve();
      if (refreshingMetrics) {
        if (!forceAfterPending) return refreshingMetrics;
        return refreshingMetrics.then(() => refreshMetrics());
      }
      refreshingMetrics = getMachineMetrics()
        .then((snapshots) => mutateMachines((current) => withMetricsSnapshot(current, snapshots)))
        .catch(() => {
          // Keep the last sample through brief auth, deploy, or network interruptions.
        })
        .finally(() => {
          refreshingMetrics = undefined;
        });
      return refreshingMetrics;
    };

    const refetch = () => {
      if (!accountFleet) return Promise.resolve([]);
      if (refreshingFleet) return refreshingFleet;
      refreshingFleet = (async () => {
        const merged = await loadMachines();
        machines.mutate(merged);
        // A registration heartbeat and its metrics event can arrive before this
        // machine exists locally. Reconcile metrics only after adding it, and
        // wait out any snapshot request that started against the old fleet.
        await refreshMetrics(true);
        return machines() ?? merged;
      })().finally(() => {
        refreshingFleet = undefined;
      });
      return refreshingFleet;
    };
    const refetchQuietly = () => void refetch().catch(() => undefined);

    const metricsStream = createFleetEventStreamController({
      visible: pageIsVisible,
      onEvent: (type, properties, signal) => {
        const subscribed = type === "server.connected" ? subscribedEvents(properties) : undefined;
        if (type === "server.connected" || type === "fleet.machine.updated" || type === "fleet.machine.deleted") {
          void (async () => {
            // An in-flight snapshot can predate this event; refresh after it settles.
            await refreshingFleet?.catch(() => undefined);
            if (signal.aborted) return;
            // A (re)connect refreshes only when an event may have been missed
            // between the last snapshot and the stream's subscription.
            if (type === "server.connected" && eventCursorReached(snapshotEvents, subscribed)) return;
            await refetch();
          })().catch(() => undefined);
        }
        if (type === "server.connected") {
          void invalidateProviders().catch(() => undefined);
          invalidateProviderUsage();
        }
        // A (re)connect may have missed a machine's report.
        if (type === "automations.updated" || type === "server.connected") invalidateAutomations();
        if (type === "workspaces.updated" || type === "server.connected") invalidateWorkspaceInventory();
        if (type === "capabilities.updated") {
          const id = (properties as { id?: unknown } | undefined)?.id;
          applyCapabilityLibraryChange({ kind: "capability", id: typeof id === "string" ? id : undefined });
        }
        // A (re)connect may have missed any change, so revalidate the whole library.
        if (type === "server.connected") applyCapabilityLibraryChange({ kind: "capability" });
        // Tool readiness is reported per machine, so fleet membership changes it too.
        if (type === "tools.updated" || type === "fleet.machine.updated" || type === "fleet.machine.registered" || type === "fleet.machine.deleted") {
          applyCapabilityLibraryChange({ kind: "tools" });
        }
        if (type === "providers.updated") {
          void invalidateProviders().catch(() => undefined);
        }
        // A (re)connect may have missed a settings change on another device.
        if (type === "preferences.updated") invalidateAccountPreferences();
        if (type === "mux-oracle.updated") applyAccountMuxOracleChange(properties as { runner?: unknown } | undefined);
        if (type === "server.connected") applyAccountMuxOracleChange();
        if (type === "server.connected") invalidateAccountPreferences(subscribed);
        if (type === "providers.usage.updated") {
          invalidateProviderUsage();
        }
        if (type === "fleet.metrics.updated") {
          const metrics = properties as FleetMachineMetrics;
          if (metrics?.name) mutateMachines((current) => withMetrics(current, metrics));
        }
        if (type === "fleet.heartbeat.updated") {
          const heartbeat = properties as FleetHeartbeatUpdate;
          if (heartbeat?.name) {
            mutateMachines((current) => withHeartbeat(current, heartbeat));
            if (!(machines() ?? []).some((machine) => machine.name === heartbeat.name)) refetchQuietly();
          }
        }
        if (type === "fleet.update.updated") {
          const update = properties as FleetUpdate;
          if (update?.name) mutateMachines((current) => withUpdate(current, update));
        }
        const enrollment = enrollmentProgressFromFleetEvent(type, properties);
        if (enrollment) {
          setEnrollmentProgress(enrollment);
          if (enrollment.phase === "registered") refetchQuietly();
        }
      },
    });

    // Each machine's measurements, kept while it is off screen so showing it
    // again neither forgets its fastest route nor waits to measure.
    const comparisons = new Map<string, ReturnType<typeof createRouteComparisonController>>();
    const comparison = (name: string) => {
      let controller = comparisons.get(name);
      if (!controller) {
        controller = createRouteComparisonController();
        comparisons.set(name, controller);
      }
      return controller;
    };
    const syncRouteComparisons = () => {
      const list = standalone ? [] : machines() ?? [];
      const names = shown();
      const visible = pageIsVisible();
      for (const [name, controller] of comparisons) {
        if (list.some((machine) => machine.name === name)) continue;
        controller.dispose();
        comparisons.delete(name);
      }
      for (const machine of list) {
        const on = names.includes(machine.name);
        if (on || comparisons.has(machine.name)) comparison(machine.name).sync(on ? machine : undefined, visible);
      }
    };
    createEffect(() => {
      syncRouteComparisons();
      const list = machines();
      if (!list) return;
      const next: Record<string, FleetMachineHealth | undefined> = {};
      for (const machine of list) {
        next[machineKey(machine.public_url)] = machine.health;
      }
      setHealth(reconcile(next));
      const names = shown();
      untrack(() => accessController.sync(names, pageIsVisible()));
    });

    // A shown machine's connection follows its pinned route, else its fastest
    // measured one, else the one last measured fastest; until any exists, the
    // control plane's order.
    createEffect(() => {
      if (standalone) return;
      for (const name of shown()) {
        const machine = machines()?.find(item => item.name === name);
        if (!machine) continue;
        const measured = fastestProvider(
          comparison(name).snapshot(machine).rounds,
          (machine.public_routes ?? []).map(route => route.provider),
          untrack(() => machineRouteManager.preferredProvider(machine)),
        );
        if (measured) untrack(() => routeMemory.remember(machine.name, measured));
        const route = preferredPublicRoute(machine, {
          pinned: routePins.pinned(machine.name),
          fastest: measured ?? untrack(() => routeMemory.fastest(machine.name)),
        });
        untrack(() => routes.preferRoute(machine.name, route?.url));
      }
    });

    const onVisibility = () => {
      syncRouteComparisons();
      const names = untrack(shown);
      if (!pageIsVisible()) {
        accessController.sync(names, false);
        routes.cancelDiscovery();
        metricsStream.pause();
        return;
      }
      setHealthClock(Math.floor(Date.now() / 1000));
      // Runs before the connection gates' own listeners, so their retries renew access at once.
      accessController.forgetNetworkFailures();
      accessController.sync(names, true);
      if (accountFleet) metricsStream.resume();
      void refreshMetrics();
      routes.discoverBetterRoute(true);
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onVisibility);
    // A machine-events stream that slept with the device is replaced on wake.
    const stopWakeWatch = watchSystemWake(onVisibility);
    // Loopback is probed only on the events above, never on a timer.
    const preferredRouteTimer = setInterval(() => { if (pageIsVisible()) routes.discoverBetterRoute(false); }, preferredRouteCheckMs);
    if (accountFleet) metricsStream.resume();
    onCleanup(() => {
      stopWakeWatch();
      clearInterval(preferredRouteTimer);
      for (const controller of comparisons.values()) controller.dispose();
      comparisons.clear();
      metricsStream.dispose();
      clearInterval(healthClockTimer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onVisibility);
    });

    // The health clock reruns this every few seconds; reusing unchanged views
    // keeps their rows mounted and spares every reader a no-op update.
    const views = createMemo<FleetMachineView[]>((previous) => reuseEqual(previous, buildViews()));
    function buildViews(): FleetMachineView[] {
      const now = healthClock();
      const registered = (machines() ?? []).map((machine) => {
        const key = machineKey(machine.public_url);
        const current = { ...machine, health: health[key] };
        return { ...current, key, health: effectiveMachineHealth(current, now, (liveStreams[key] ?? 0) > 0, offlineAfter) };
      });
      const current = server.current;
      if ((!localDevelopment && !standalone) || !current) return registered;
      const key = ServerConnection.key(current);
      const direct: FleetMachineView = {
        name: standalone ? "local" : "Local Go backend",
        public_url: current.http.url,
        local_url: current.http.url,
        updated_at: startedAt,
        direct: true,
        key,
        health: {
          state: "ready",
          label: "Connected",
          message: standalone ? "Running on this machine." : "Direct local development connection.",
        },
      };
      return [direct, ...registered.filter((machine) => machine.key !== key)];
    }
    const ready = createMemo(() =>
      views().filter((machine) => machineReady(machine.health)),
    );
    const connected = createMemo(() => ready().length > 0);
    // Providers come from a machine: list them again once one is ready.
    createEffect((wasConnected: boolean) => {
      const now = connected();
      if (now && !wasConnected) void invalidateProviders().catch(() => undefined);
      return now;
    }, false);
    const byKey = (key?: ServerConnection.Key) =>
      views().find((machine) => machine.key === key);
    // Providers and saved secrets live on the account's machines; any ready
    // one serves them. Keep using the last one while it is ready, so a sign-in
    // finishes on the machine it started on, else the selected one.
    let accountMachine: string | undefined;
    onCleanup(setAccountMachineResolver(async (signal) => {
      const candidates = ready().map((machine) => machine.name);
      const selected = byKey(server.key)?.name;
      const order = [accountMachine, selected, ...candidates].filter(
        (name, index, names): name is string => !!name && candidates.includes(name) && names.indexOf(name) === index,
      );
      for (const name of order) {
        const connection = await ensureMachineConnection(name, signal).catch(() => undefined);
        if (connection) {
          accountMachine = name;
          return connection.http;
        }
      }
      return undefined;
    }));
    /**
     * How people know the machine behind a connection: its display name, never
     * its route name. Undefined until the fleet lists the machine.
     */
    const label = (key?: ServerConnection.Key) => {
      const view = byKey(key);
      return view && formatMachineDisplayName(view.name, view.display_name);
    };

    return {
      machines,
      views,
      ready,
      connected,
      byKey,
      label,
      /** Marks the machine's own event stream live until the returned release runs. */
      reportLiveConnection(key: ServerConnection.Key) {
        setLiveStreams(key, (count) => (count ?? 0) + 1);
        let released = false;
        return () => {
          if (released) return;
          released = true;
          setLiveStreams(key, (count) => Math.max(0, (count ?? 1) - 1));
        };
      },
      /** Sets the machines on screen, which keep their access renewed and their routes measured. */
      setShownMachines: (names: readonly string[]) => setShown(names),
      routeComparison: (machine: FleetMachine) => comparison(machine.name).snapshot(machine),
      /** The tunnel provider pinned for a machine in this browser, if any. */
      routePin: routePins.pinned,
      /** Pins a machine's tunnel provider in this browser, or with undefined returns it to the fastest. */
      pinRoute: (name: string, provider: string | undefined) => {
        machineRouteManager.prefer(name, undefined);
        routePins.pin(name, provider);
      },
      /** Why this browser's last check of a machine's local connection failed, if it did. */
      localRouteFailure: machineRouteManager.localFailure,
      health: (key: ServerConnection.Key) => health[key],
      removeOptimistic(name: string) {
        const current = machines() ?? [];
        const machine = current.find((item) => item.name === name);
        if (!machine) return;
        machineRouteManager.invalidate(name);
        routePins.pin(name, undefined);
        machines.mutate(current.filter((item) => item.name !== name));
        seedFleetSnapshot(machines() ?? []);
        return { machine, index: current.indexOf(machine) };
      },
      restoreOptimistic(snapshot: { machine: FleetMachine; index: number }) {
        const current = machines() ?? [];
        if (current.some((item) => item.name === snapshot.machine.name)) return;
        const next = current.slice();
        next.splice(Math.min(snapshot.index, next.length), 0, snapshot.machine);
        machines.mutate(next);
        seedFleetSnapshot(next);
      },
      enrollmentProgress,
      startedEmpty,
      clearEnrollmentProgress: () => setEnrollmentProgress(undefined),
      refetch,
      ensureMachineAccess,
      machineConnection,
      ensureMachineConnection,
      installTransport,
      forgetNetworkFailures: accessController.forgetNetworkFailures,
      refreshMetrics,
    };
  },
});

/**
 * Whether the machine this code serves can be used: its scope's machine, or
 * the selected machine outside any scope.
 */
export function useMachineReady() {
  const fleet = useFleet();
  const server = useServer();
  return () => machineReady(fleet.byKey(server.key)?.health);
}
